// src/feature-module/support/SupportCases.tsx
//
// Case list + the review view.
//
// The detail page is the one that matters. It is not a ticket screen — it is
// the answer to "what did support do to this tenant, and why": the timeline,
// the impersonation grants and repairs performed under the case, and the audit
// rows for every correlation id the tenant quoted.

import React, { useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  Card, Table, Tag, Button, Modal, Form, Input, Select, Space, Descriptions,
  Timeline, Empty, Spin, message, Typography,
} from "antd";
import { PlusOutlined, ArrowLeftOutlined } from "@ant-design/icons";
import {
  useListSupportCasesQuery,
  useGetSupportCaseQuery,
  useCreateSupportCaseMutation,
  useUpdateSupportCaseMutation,
  useAddCaseNoteMutation,
  useLinkCaseRequestIdMutation,
  useNotifyTenantFromCaseMutation,
} from "../../core/redux/services/adminSupportApi";
import { useGetTenantsQuery } from "../../core/redux/services/adminTenantApi";
import SupportActions from "./SupportActions";

const STATUS_COLOR: Record<string, string> = {
  OPEN: "red", INVESTIGATING: "orange", WAITING_ON_TENANT: "gold", RESOLVED: "green", CLOSED: "default",
};
const SEVERITY_COLOR: Record<string, string> = { LOW: "default", NORMAL: "blue", HIGH: "orange", CRITICAL: "red" };
const STATUSES = ["OPEN", "INVESTIGATING", "WAITING_ON_TENANT", "RESOLVED", "CLOSED"];
const SEVERITIES = ["LOW", "NORMAL", "HIGH", "CRITICAL"];

/** Event types carry different weight — a repair is not a note. */
const EVENT_COLOR: Record<string, string> = {
  NOTE: "gray", STATUS_CHANGE: "blue", IMPERSONATION: "orange",
  REPAIR: "red", TENANT_NOTIFIED: "green", REQUEST_LINKED: "purple",
};

const dt = (v: any) => (v ? new Date(v).toLocaleString() : "—");
const err = (e: any, f: string) => e?.data?.message || f;

// ─── List ────────────────────────────────────────────────────────────────────

export const SupportCasesList: React.FC = () => {
  const navigate = useNavigate();
  const [status, setStatus] = useState<string | undefined>();
  const [tenantId, setTenantId] = useState<string | undefined>();
  const [open, setOpen] = useState(false);
  const [form] = Form.useForm();

  const { data, isFetching } = useListSupportCasesQuery({ status, tenantId });
  const { data: tenants = [] } = useGetTenantsQuery();
  const [create, createState] = useCreateSupportCaseMutation();

  const onCreate = async () => {
    try {
      const v = await form.validateFields();
      const created = await create(v).unwrap();
      message.success(`Opened ${created.reference}`);
      setOpen(false);
      form.resetFields();
      navigate(`/support-cases/${created.id}`);
    } catch (e: any) {
      if (e?.errorFields) return;
      message.error(err(e, "Could not open the case."));
    }
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div>
            <h4 className="mb-1">Support Cases</h4>
            <p className="text-muted mb-0">Work on a tenant, and the record of what was done</p>
          </div>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setOpen(true)}>New case</Button>
        </div>

        <Card>
          <Space wrap className="mb-3">
            <Select
              allowClear placeholder="All statuses" style={{ minWidth: 190 }}
              value={status} onChange={setStatus}
              options={STATUSES.map((s) => ({ value: s, label: s.replace(/_/g, " ") }))}
            />
            <Select
              allowClear showSearch optionFilterProp="label" placeholder="All tenants" style={{ minWidth: 230 }}
              value={tenantId} onChange={setTenantId}
              options={tenants.map((t: any) => ({ value: t.id, label: t.name || t.id }))}
            />
          </Space>

          <Table
            rowKey="id"
            size="small"
            loading={isFetching}
            dataSource={data?.data ?? []}
            onRow={(r) => ({ onClick: () => navigate(`/support-cases/${r.id}`), style: { cursor: "pointer" } })}
            pagination={{ pageSize: 25, showTotal: (t) => `${t} cases` }}
            columns={[
              { title: "Ref", dataIndex: "reference", width: 130, render: (v: string) => <Typography.Text code>{v}</Typography.Text> },
              { title: "Title", dataIndex: "title", ellipsis: true },
              {
                title: "Tenant", dataIndex: "tenantId", ellipsis: true,
                render: (v: string) => tenants.find((t: any) => t.id === v)?.name ?? v,
              },
              { title: "Severity", dataIndex: "severity", width: 110, render: (v: string) => <Tag color={SEVERITY_COLOR[v]}>{v}</Tag> },
              { title: "Status", dataIndex: "status", width: 160, render: (v: string) => <Tag color={STATUS_COLOR[v]}>{v?.replace(/_/g, " ")}</Tag> },
              { title: "Events", dataIndex: ["_count", "events"], width: 80 },
              { title: "Opened", dataIndex: "createdAt", width: 175, render: dt },
            ]}
          />
        </Card>

        <Modal title="Open a support case" open={open} onCancel={() => setOpen(false)} onOk={onCreate} confirmLoading={createState.isLoading}>
          <Form form={form} layout="vertical" initialValues={{ severity: "NORMAL" }}>
            <Form.Item name="tenantId" label="Tenant" rules={[{ required: true }]}>
              <Select showSearch optionFilterProp="label" placeholder="Which tenant?"
                options={tenants.map((t: any) => ({ value: t.id, label: t.name || t.id }))} />
            </Form.Item>
            <Form.Item name="title" label="Title" rules={[{ required: true, min: 5 }]}>
              <Input placeholder="What is the tenant reporting?" />
            </Form.Item>
            <Form.Item name="severity" label="Severity">
              <Select options={SEVERITIES.map((s) => ({ value: s, label: s }))} />
            </Form.Item>
            <Form.Item name="description" label="Details">
              <Input.TextArea rows={3} />
            </Form.Item>
          </Form>
        </Modal>
      </div>
    </div>
  );
};

// ─── Detail ──────────────────────────────────────────────────────────────────

export const SupportCaseDetailPage: React.FC = () => {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const { data, isFetching } = useGetSupportCaseQuery(id, { skip: !id });
  const [update] = useUpdateSupportCaseMutation();
  const [addNote, noteState] = useAddCaseNoteMutation();
  const [linkReq] = useLinkCaseRequestIdMutation();
  const [notify, notifyState] = useNotifyTenantFromCaseMutation();

  const [note, setNote] = useState("");
  const [reqId, setReqId] = useState("");
  const [msgOpen, setMsgOpen] = useState(false);
  const [msgForm] = Form.useForm();

  if (isFetching && !data) return <div className="page-wrapper"><div className="content"><Spin /> Loading case…</div></div>;
  if (!data) return <div className="page-wrapper"><div className="content"><Empty description="Case not found" /></div></div>;

  const c = data.case;

  const act = async (fn: () => Promise<any>, ok: string, fail: string) => {
    try { await fn(); message.success(ok); } catch (e: any) { message.error(err(e, fail)); }
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        <Space className="mb-2">
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate("/support-cases")}>Cases</Button>
        </Space>

        <Card className="mb-3">
          <div className="d-flex justify-content-between align-items-start flex-wrap gap-2">
            <div>
              <h4 className="mb-1">
                <Typography.Text code>{c.reference}</Typography.Text> {c.title}
              </h4>
              <Space wrap>
                <Tag color={SEVERITY_COLOR[c.severity]}>{c.severity}</Tag>
                <Tag color={STATUS_COLOR[c.status]}>{c.status?.replace(/_/g, " ")}</Tag>
                {data.tenant && <Link to={`/tenants/${data.tenant.id}/360`}>{data.tenant.name}</Link>}
              </Space>
            </div>
            <Space wrap>
              <Select
                value={c.status} style={{ minWidth: 180 }}
                onChange={(v) => act(() => update({ id, status: v }).unwrap(), `Status → ${v}`, "Could not update status.")}
                options={STATUSES.map((s) => ({ value: s, label: s.replace(/_/g, " ") }))}
              />
              <Button onClick={() => setMsgOpen(true)}>Message tenant</Button>
              {data.tenant && (
                <SupportActions
                  tenantId={data.tenant.id}
                  tenantName={data.tenant.name}
                  tenantType={data.tenant.type}
                  caseId={id}
                />
              )}
            </Space>
          </div>
          {c.description && <p className="text-muted mt-3 mb-0">{c.description}</p>}
        </Card>

        <div className="row g-3">
          <div className="col-lg-7">
            <Card title="Timeline" className="mb-3">
              <Timeline
                items={(c.events ?? []).map((e) => ({
                  color: EVENT_COLOR[e.type] ?? "gray",
                  children: (
                    <div>
                      <Space size={6} wrap>
                        <Tag color={EVENT_COLOR[e.type] ?? "default"}>{e.type.replace(/_/g, " ")}</Tag>
                        <span className="text-muted small">{dt(e.createdAt)}</span>
                        {e.actorEmail && <span className="text-muted small">· {e.actorEmail}</span>}
                      </Space>
                      {e.body && <div style={{ marginTop: 4 }}>{e.body}</div>}
                    </div>
                  ),
                }))}
              />
              <Space.Compact style={{ width: "100%", marginTop: 12 }}>
                <Input
                  placeholder="Add a note…" value={note} onChange={(e) => setNote(e.target.value)}
                  onPressEnter={() => note.trim() && act(async () => { await addNote({ id, body: note }).unwrap(); setNote(""); }, "Note added", "Could not add the note.")}
                />
                <Button
                  type="primary" loading={noteState.isLoading} disabled={!note.trim()}
                  onClick={() => act(async () => { await addNote({ id, body: note }).unwrap(); setNote(""); }, "Note added", "Could not add the note.")}
                >
                  Add
                </Button>
              </Space.Compact>
            </Card>
          </div>

          <div className="col-lg-5">
            {/* The review payload: what was actually done under this case. */}
            <Card title="Done under this case" className="mb-3" size="small">
              <Descriptions size="small" column={1} bordered>
                <Descriptions.Item label="Impersonation grants">
                  {data.impersonations.length || <span className="text-muted">none</span>}
                </Descriptions.Item>
                <Descriptions.Item label="KYC exemptions">
                  {data.exemptions.length || <span className="text-muted">none</span>}
                </Descriptions.Item>
                <Descriptions.Item label="Linked audit rows">
                  {data.linkedActivity.length || <span className="text-muted">none</span>}
                </Descriptions.Item>
              </Descriptions>

              {data.impersonations.map((i) => (
                <div key={i.id} className="mt-2 small">
                  <Tag color="orange">SESSION</Tag> {i.impersonatorEmail}
                  {i.readOnly && <Tag className="ms-1">read-only</Tag>}
                  <div className="text-muted">{i.reason}</div>
                </div>
              ))}
              {data.exemptions.map((x: any) => (
                <div key={x.id} className="mt-2 small">
                  <Tag color="red">KYC WAIVED</Tag> until {dt(x.expiresAt)}
                  <div className="text-muted">{x.reason}</div>
                </div>
              ))}
            </Card>

            <Card title="Correlation IDs" size="small" className="mb-3">
              <p className="text-muted small">
                The ids the tenant quoted from their errors. Each one joins to the audit rows and server errors for that request.
              </p>
              {c.requestIds.length === 0 && <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="none linked" />}
              {c.requestIds.map((r) => (
                <div key={r}>
                  <Typography.Text code copyable style={{ fontSize: 11 }}>{r}</Typography.Text>
                </div>
              ))}
              <Space.Compact style={{ width: "100%", marginTop: 10 }}>
                <Input placeholder="Paste a request id…" value={reqId} onChange={(e) => setReqId(e.target.value)} />
                <Button
                  disabled={!reqId.trim()}
                  onClick={() => act(async () => { await linkReq({ id, requestId: reqId }).unwrap(); setReqId(""); }, "Linked", "Could not link that id.")}
                >
                  Link
                </Button>
              </Space.Compact>
            </Card>
          </div>
        </div>

        <Modal
          title="Message the tenant"
          open={msgOpen}
          onCancel={() => setMsgOpen(false)}
          confirmLoading={notifyState.isLoading}
          onOk={async () => {
            try {
              const v = await msgForm.validateFields();
              await notify({ id, ...v }).unwrap();
              message.success("Sent to the tenant's administrators.");
              setMsgOpen(false); msgForm.resetFields();
            } catch (e: any) { if (!e?.errorFields) message.error(err(e, "Could not send.")); }
          }}
        >
          <p className="text-muted">
            Delivered as an in-app notification to the tenant's administrators, and recorded on this case.
          </p>
          <Form form={msgForm} layout="vertical">
            <Form.Item name="title" label="Title (optional)">
              <Input placeholder={`Support update — ${c.reference}`} />
            </Form.Item>
            <Form.Item name="message" label="Message" rules={[{ required: true }]}>
              <Input.TextArea rows={4} placeholder="What should the tenant know?" />
            </Form.Item>
          </Form>
        </Modal>
      </div>
    </div>
  );
};

export default SupportCasesList;
